import {Component, OnInit} from "@angular/core";
import {CompareService} from "./compare.service";
import {environment} from "../../../../../environments/environment";
import {from} from "rxjs";
import {switchMap} from "rxjs/operators";

@Component({
  standalone: false,
  selector: 'app-compare',
  templateUrl: './compare.component.html',
  styleUrls: ['./compare.component.scss']
})
export class CompareComponent implements OnInit {
  compareItems: any[] = [];
  attributeCodes: string[] = [];
  environment: any;
  url: any;
  loading = true;
  error = '';

  constructor(private compareService: CompareService) {
  }

  ngOnInit() {
    const items = this.compareService.getCompareItems();

    if (!items || items.length < 2) {
      this.loading = false;
      this.error = 'Select at least two listings to compare.';
      return;
    }

    const id1 = items[0]?.id;
    const id2 = items[1]?.id;
    const id3 = items.length > 2 ? items[2]?.id : '';

    if (!id1 || !id2) {
      this.loading = false;
      this.error = 'Compare selection is incomplete. Go back and add listings again.';
      return;
    }

    from(environment).pipe(
      switchMap((env: any) => {
        this.environment = env;
        this.url = env.backendURL;
        return this.compareService.getItems(`${this.url}/item/compare`, id1, id2, id3 || '');
      })
    ).subscribe({
      next: (response: any) => {
        this.compareItems = Array.isArray(response) ? response : [];
        this.attributeCodes = this.collectAttributeCodes(this.compareItems);
        this.loading = false;
        if (!this.compareItems.length) {
          this.error = 'Could not load those listings for comparison.';
        }
      },
      error: (err) => {
        console.error('Compare failed', err);
        this.loading = false;
        this.error = 'Failed to load comparison. Please try again.';
      }
    });
  }

  listingTitle(item: any): string {
    return item?.item?.name || item?.name || 'Listing';
  }

  listingPrice(item: any): number | null {
    const value = item?.item?.value ?? item?.value;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  locationLabel(item: any): string {
    const loc = item?.item?.location ?? item?.location;
    if (!loc) {
      return '—';
    }
    if (typeof loc === 'string') {
      return loc;
    }
    return loc.name || loc.county || '—';
  }

  categoryLabel(item: any): string {
    const bc = item?.item?.bc || item?.bc;
    if (!bc) {
      return '—';
    }
    const parts = [bc?.c?.name, bc?.s?.name, bc?.ss?.name].filter(Boolean);
    return parts.length ? parts.join(' / ') : '—';
  }

  attributeValue(item: any, code: string): string {
    const attrs = item?.attributes || item?.item?.attributes || [];
    if (Array.isArray(attrs)) {
      const match = attrs.find((a: any) => a?.code === code || a?.attributeCode === code);
      return match?.displayValue || match?.value || '—';
    }
    if (attrs && typeof attrs === 'object') {
      return attrs[code] != null ? `${attrs[code]}` : '—';
    }
    return '—';
  }

  attributeLabel(code: string): string {
    for (const item of this.compareItems || []) {
      const attrs = item?.attributes || [];
      if (Array.isArray(attrs)) {
        const match = attrs.find((a: any) => a?.code === code || a?.attributeCode === code);
        if (match?.label || match?.name) {
          return match.label || match.name;
        }
      }
    }
    return code;
  }

  private collectAttributeCodes(items: any[]): string[] {
    const codes = new Set<string>();
    for (const item of items || []) {
      const attrs = item?.attributes || item?.item?.attributes || [];
      if (Array.isArray(attrs)) {
        for (const a of attrs) {
          const code = a?.code || a?.attributeCode;
          if (code) {
            codes.add(code);
          }
        }
      } else if (attrs && typeof attrs === 'object') {
        Object.keys(attrs).forEach(code => codes.add(code));
      }
    }
    return Array.from(codes).slice(0, 12);
  }
}
